import { BatchState, BatchAction, ReferralItem } from "./types";

export function batchReducer(state: BatchState, action: BatchAction): BatchState {
  switch (action.type) {
    case "ADD_FILES": {
      const newItems: ReferralItem[] = action.files.map(file => ({
        id: `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        status: "pending",
      }));
      return { step: "processing", items: newItems };
    }

    case "EXTRACTION_START": {
      if (state.step !== "processing") return state;
      return {
        ...state,
        items: state.items.map(item => (item.id === action.id ? { ...item, status: "extracting" } : item)),
      };
    }

    case "EXTRACTION_COMPLETE": {
      if (state.step !== "processing") return state;
      const items = state.items.map(item =>
        item.id === action.id
          ? {
              ...item,
              status: "extracted" as const,
              extracted: action.extracted,
              rawText: action.rawText,
              validationIssues: action.validationIssues,
            }
          : item
      );
      const allDone = items.every(i => i.status === "extracted");
      if (allDone) {
        return { step: "review", items, currentIndex: 0 };
      }
      return { ...state, items };
    }

    case "UPDATE_FIELDS": {
      if (state.step !== "review") return state;
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.id ? { ...item, extracted: { ...item.extracted, ...action.fields } } : item
        ),
      };
    }

    case "REMOVE_ITEM": {
      if (state.step !== "review" && state.step !== "summary") return state;
      const items = state.items.filter(item => item.id !== action.id);
      if (items.length === 0) {
        return { step: "empty" };
      }
      if (state.step === "review") {
        const newIndex = Math.min(state.currentIndex, items.length - 1);
        return { ...state, items, currentIndex: newIndex };
      }
      return { ...state, items };
    }

    case "START_REVIEW": {
      if (state.step !== "processing" && state.step !== "summary") return state;
      return { step: "review", items: state.items, currentIndex: 0 };
    }

    case "GO_TO_ITEM": {
      if (state.step !== "summary") return state;
      return { step: "review", items: state.items, currentIndex: action.index };
    }

    case "NEXT_ITEM": {
      if (state.step !== "review") return state;
      const nextIndex = state.currentIndex + 1;
      if (nextIndex >= state.items.length) {
        return { step: "summary", items: state.items };
      }
      return { ...state, currentIndex: nextIndex };
    }

    case "PREV_ITEM": {
      if (state.step !== "review") return state;
      return { ...state, currentIndex: Math.max(0, state.currentIndex - 1) };
    }

    case "GO_TO_SUMMARY": {
      if (state.step !== "review") return state;
      return { step: "summary", items: state.items };
    }

    case "START_SUBMIT": {
      if (state.step !== "summary") return state;
      const totalToSubmit = state.items.filter(i => i.status === "extracted").length;
      return { step: "submitting", items: state.items, currentIndex: 0, submittedCount: 0, totalToSubmit };
    }

    case "SUBMIT_SUCCESS": {
      if (state.step !== "submitting") return state;
      const items = state.items.map(item => (item.id === action.id ? { ...item, status: "submitted" as const } : item));
      const nextPending = items.findIndex((i, idx) => idx > state.currentIndex && i.status === "extracted");
      const submittedCount = state.submittedCount + 1;
      if (nextPending === -1) {
        return { step: "success", submitted: submittedCount, failed: 0 };
      }
      return { ...state, items, currentIndex: nextPending, submittedCount };
    }

    case "SUBMIT_ERROR": {
      if (state.step !== "submitting") return state;
      const items = state.items.map(item =>
        item.id === action.id
          ? { ...item, validationIssues: [{ field: "file" as const, type: "error" as const, message: action.error }] }
          : item
      );
      const nextPending = items.findIndex((i, idx) => idx > state.currentIndex && i.status === "extracted");
      if (nextPending === -1) {
        const submitted = items.filter(i => i.status === "submitted").length;
        const failed = state.totalToSubmit - submitted;
        return { step: "success", submitted, failed };
      }
      return { ...state, items, currentIndex: nextPending };
    }

    case "FINISH": {
      if (state.step === "submitting") {
        const submitted = state.items.filter(i => i.status === "submitted").length;
        const failed = state.items.filter(i => i.status !== "submitted").length;
        return { step: "success", submitted, failed };
      }
      if (state.step === "summary") {
        return { step: "success", submitted: 0, failed: state.items.length };
      }
      return { step: "success", submitted: 0, failed: 0 };
    }

    case "RESET":
      return { step: "empty" };

    default:
      return state;
  }
}

export const initialState: BatchState = { step: "empty" };
